import { useState, useEffect } from 'react';
import { adminAPI } from '@/services/api';
import toast from 'react-hot-toast';

const InfoRow = ({ label, value, icon }) => (
  <div className="flex items-start gap-4 p-4 hover:bg-slate-50 rounded-xl transition-colors">
    <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
      <i className={`fas ${icon}`} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">{label}</p>
      <p className="text-sm font-semibold text-slate-800 mt-0.5 break-words">{value || '—'}</p>
    </div>
  </div>
);

const StaffProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await adminAPI.getProfile();
        setProfile(res.data.data);
      } catch (err) {
        console.error('Profile fetch error:', err);
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center text-slate-400 py-12">
        <i className="fas fa-user-slash text-4xl mb-4 block" />
        <p>Failed to load profile</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-black text-slate-800">My Profile</h2>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-800 p-8 text-white relative">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-4 right-10 w-32 h-32 border-4 border-white rounded-full" />
            <div className="absolute bottom-4 left-10 w-24 h-24 border-4 border-white rounded-full" />
          </div>
          <div className="relative z-10 flex items-center gap-6">
            <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-4xl font-black border-4 border-white/30">
              {(profile.full_name || 'A').charAt(0)}
            </div>
            <div>
              <h3 className="text-2xl font-black">{profile.full_name || 'Admin'}</h3>
              <p className="text-emerald-100 text-sm mt-1 capitalize">{profile.role || 'Staff'}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-semibold backdrop-blur-sm">
                  {profile.department || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-2">
            <InfoRow label="Full Name" value={profile.full_name} icon="fa-user" />
            <InfoRow label="Email Address" value={profile.email} icon="fa-envelope" />
            <InfoRow label="Role" value={profile.role} icon="fa-id-badge" />
            <InfoRow label="Department" value={profile.department} icon="fa-building" />
            <InfoRow label="Designation" value={profile.designation} icon="fa-briefcase" />
          </div>
        </div>
      </div>

      {/* Edit Profile Card */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-slate-800">Edit Profile</h3>
          <button
            onClick={() => {
              setIsEditing(!isEditing);
              setEditForm({
                full_name: profile.full_name,
                phone: profile.phone || ''
              });
            }}
            className="px-4 py-2 rounded-xl font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </button>
        </div>

        {isEditing ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name</label>
              <input
                type="text"
                value={editForm.full_name || ''}
                onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">Phone Number</label>
              <input
                type="text"
                value={editForm.phone || ''}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all"
              />
            </div>
            <div className="md:col-span-2 flex justify-end pt-4">
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                  try {
                    setSaving(true);
                    await adminAPI.updateProfile(editForm);
                    toast.success('Profile updated successfully');
                    setIsEditing(false);
                    // Refresh profile
                    const res = await adminAPI.getProfile();
                    setProfile(res.data.data);
                  } catch (err) {
                    console.error('Update error:', err);
                    toast.error(err.response?.data?.message || 'Failed to update profile');
                  } finally {
                    setSaving(false);
                  }
                }}
                className="w-full md:w-auto px-8 py-4 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-800 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fas fa-save"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <p className="text-slate-500 text-sm">Click Edit Profile to update your personal information</p>
        )}
      </div>

      {/* Change Password Card */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6">
        <h3 className="text-lg font-bold text-slate-800 mb-6">Change Password</h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Current Password</label>
            <input
              type="password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">New Password</label>
            <input
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Confirm New Password</label>
            <input
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none transition-all"
            />
          </div>
          <div className="md:col-span-3 flex justify-end pt-4">
              <button
                type="button"
                disabled={saving}
                onClick={async () => {
                if (passwordForm.new_password !== passwordForm.confirm_password) {
                  toast.error('Passwords do not match');
                  return;
                }
                try {
                  setSaving(true);
                  await adminAPI.changePassword(passwordForm);
                  toast.success('Password changed successfully');
                  setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
                } catch (err) {
                  toast.error(err.response?.data?.message || 'Failed to change password');
                } finally {
                  setSaving(false);
                }
              }}
              className="px-6 py-3 rounded-xl font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-70"
            >
              {saving ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffProfile;

